import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Banners } from '@sorare/core/src/components/layout/Banners';
import { MobileAppBottomPortalReceiver } from '@sorare/core/src/components/navigation/AppNavigation';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import { FiltersGroup } from '@sorare/core/src/lib/filters';
import { metadatas } from '@sorare/core/src/lib/seo/football';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDetails } from 'components/player/PlayerDrawer';
import useDefaultFilters from 'hooks/useDefaultFilters';
import { AdvancedCardSearch } from 'searchCards';

import NewsSignings from '../OnboardingDialog/NewsSignings';
import PageTemplate from '../PageTemplate';
import { StarUniqueAuctionCarousel } from '../StarUniqueAuctionCarousel';

type Props = {
  primaryMarketFilters: FiltersGroup[];
  banner: ReactNode;
  PlayerDetailsComponents: PlayerDetails;
};

const CarouselNegativeMargin = styled.div`
  margin: 0 calc(-1 * var(--intermediate-unit));
  @media ${tabletAndAbove} {
    margin: 0 calc(-1 * var(--triple-unit));
  }
`;

const NewSignings = ({
  primaryMarketFilters,
  banner,
  PlayerDetailsComponents,
}: Props) => {
  const { isIosApp } = useIsMobileApp();
  const [forceShowOnboardingDialog, setForceShowOnboardingDialog] =
    useState(false);
  useTitleAndDescription(
    metadatas.newSignings.title,
    metadatas.newSignings.description
  );
  const filters = useDefaultFilters({
    primary: true,
    excludePrimaryOffers: true,
  });

  const cardSorts = createCardSorts({
    indexPrefix: 'Cards On Sale',
    withHighestAverageScore: true,
  });

  const defaultSort = 'Cards On Sale Ending Soon';

  return (
    <>
      {!isIosApp && (
        <>
          <CarouselNegativeMargin>
            <StarUniqueAuctionCarousel />
          </CarouselNegativeMargin>
          <NewsSignings
            open={forceShowOnboardingDialog}
            onClick={() => setForceShowOnboardingDialog(false)}
          />
          <Banners />
        </>
      )}

      <PageTemplate showBack>
        <AdvancedCardSearch
          sport={Sport.FOOTBALL}
          cardFilters={primaryMarketFilters}
          sorts={cardSorts}
          defaultSort={defaultSort}
          defaultFilters={filters}
          analyticsTags={['NewSignings', 'Football']}
          onlyPrimary
          removeFinishedAuctions
          hideSorareUser
          banner={banner}
          PlayerDetailsComponent={PlayerDetailsComponents}
          distinct
        />
      </PageTemplate>
      {isIosApp && (
        <MobileAppBottomPortalReceiver id="above-bottom-bar-portal" />
      )}
    </>
  );
};

export default NewSignings;
